import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Rectangle,
} from "recharts";
import { useNavigate } from "react-router-dom";

interface DayRow {
  orderDayNumber: number;            // 1..31
  quotationCount: number | string;   // may arrive as string -> coerce
  quotationAmount: number | string;  // may arrive as string -> coerce
}

interface OrderPerDayProps {
  data: DayRow[];
}

// -------- Formatters (IST / en-IN) --------
const timeZone = "Asia/Kolkata" as const;

const fmtInt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });

const fmtMoneyAxis = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  notation: "compact",
  maximumFractionDigits: 2,
});

const fmtMoneyFull = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

const fmtFullDate = new Intl.DateTimeFormat("en-IN", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  timeZone,
});

// Normalize once: ensure numerics for Recharts & tooltips
function useChartData(data: DayRow[]) {
  return useMemo(
    () =>
      (data || []).map((d) => ({
        ...d,
        quotationCount: Number(d.quotationCount ?? 0),
        quotationAmount: Number(d.quotationAmount ?? 0),
      })),
    [data]
  );
}

export default function OrderPerDay({ data }: OrderPerDayProps) {
  const navigate = useNavigate();
  const chartData = useChartData(data);

  const handlePointClick = (payload: any) => {
    if (!payload) return;
    const orderDayNumber = Number(payload.orderDayNumber);
    if (!orderDayNumber) return;

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-based (0 = Jan)
    navigate(`/admin/quotation_list/${orderDayNumber}/${currentMonth + 1}/${currentYear}`);
  };

  if (!chartData.length) return null;

  return (
    <div style={{ width: "100%" }}>
      <ResponsiveContainer width="100%" height={340}>
        <ComposedChart data={chartData} margin={{ top: 8, right: 24, left: 8, bottom: 12 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="orderDayNumber" tickLine={false} />
          
          {/* Left Y: counts */}
          <YAxis
            yAxisId="left"
            width={52}
            allowDecimals={false}
            tickFormatter={(v) => fmtInt.format(v as number)}
          />

          {/* Right Y: money */}
          <YAxis
            yAxisId="right"
            orientation="right"
            width={84}
            tickFormatter={(v) => fmtMoneyAxis.format(v as number)}
            domain={[0, (dataMax: number) => (dataMax ? dataMax * 1.1 : 1)]}
          />

          <Tooltip
            formatter={(value: any, name: any) => {
              if (name === "quotationCount") return [fmtInt.format(Number(value || 0)), "Quotations"];
              if (name === "quotationAmount") return [fmtMoneyFull.format(Number(value || 0)), "Total Amount"];
              return [value, name];
            }}
            labelFormatter={(_, payload) => {
              const p = payload && payload[0] && (payload[0] as any).payload;
              if (!p) return "Day";
              const day = Number(p.orderDayNumber);
              const now = new Date();
              const dt = new Date(now.getFullYear(), now.getMonth(), day);
              return `Day: ${fmtFullDate.format(dt)}`;
            }}
          />

          <Legend />

          {/* Bars: discrete counts */}
          <Bar
            yAxisId="left"
            dataKey="quotationCount"
            name="Quotations"
            fill="#B3CDAD"
            radius={[6, 6, 0, 0]}
            activeBar={<Rectangle fill="#FFB3C7" stroke="#7D2E68" />}
            onClick={(d: any, _i: any) => handlePointClick((d as any)?.payload)}
          />

          {/* Line: total amount on its own scale */}
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="quotationAmount"
            name="Total Amount"
            strokeWidth={3}
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
            onClick={(d: any, _i: any) => handlePointClick((d as any)?.payload)}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
